import { FC, SyntheticEvent } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { Icon } from 'src/components';
import { ActorFace } from 'src/components/ActorFace';
import * as S from './styles';

const msgs = defineMessages({
  replay: { id: 'blob_session_replay', defaultMessage: 'Replay' },
  local: { id: 'blob_session_local', defaultMessage: 'Local speaker' },
  namedLocal: { id: 'blob_session_named_local', defaultMessage: 'Local speaker: {name}' },
  openLocalProfile: { id: 'local_profile_open', defaultMessage: "View the local's profile" },
  iKnowThis: { id: 'blob_session_i_know_this', defaultMessage: 'I know this' },
  replaySlowly: { id: 'learning_session_video_speed_play' },
  slow: { id: 'blob_session_slow', defaultMessage: 'Slow' },
  markDifficult: { id: 'blob_session_mark_difficult', defaultMessage: 'Mark as difficult' },
  difficult: { id: 'blob_session_difficult', defaultMessage: 'Difficult' },
});

export type VideoRailLocal = {
  name: string | null;
  facePictureSrc: string | null;
  /** A frame of this video stands in for the local when there's no face picture */
  videoSrc: string;
  /** Opens the local's profile; absent when the actor isn't known */
  onClick?: () => void;
};

const showMiddleFrame = ({ currentTarget: video }: SyntheticEvent<HTMLVideoElement>) => {
  if (Number.isFinite(video.duration)) {
    video.currentTime = video.duration / 2;
  }
};

const LocalAvatar: FC<{ local: VideoRailLocal }> = ({ local }) =>
  local.facePictureSrc ? (
    <ActorFace facePictureSrc={local.facePictureSrc} name={null} />
  ) : (
    <S.LocalVideoFrame aria-hidden data-testid="blob-local-video-frame">
      <video
        src={local.videoSrc}
        muted
        playsInline
        preload="metadata"
        disablePictureInPicture
        onLoadedMetadata={showMiddleFrame}
      />
    </S.LocalVideoFrame>
  );

type VideoRailProps = {
  local?: VideoRailLocal;
  onReplay?: () => void;
  slow?: { isPlayingSlowly: boolean; onReplaySlowly: () => void };
  difficult: { isDifficult: boolean; onToggle: () => void };
  onIKnowThis: () => void;
  isIKnowThisDisabled?: boolean;
  /** Sits in the page flow instead of floating over the video panel */
  inline?: boolean;
  /** Dark icons for a light background */
  light?: boolean;
};

export const VideoRail: FC<VideoRailProps> = ({
  local,
  onReplay,
  slow,
  difficult,
  onIKnowThis,
  isIKnowThisDisabled = false,
  inline = false,
  light = false,
}) => {
  const intl = useIntl();

  return (
    <S.Rail $inline={inline} $light={light} data-testid="blob-video-rail">
      {local && (
        <S.Item>
          {local.onClick ? (
            <S.LocalButton
              type="button"
              onClick={local.onClick}
              aria-label={intl.formatMessage(msgs.openLocalProfile)}
              data-testid="blob-local"
            >
              <LocalAvatar local={local} />
            </S.LocalButton>
          ) : (
            <S.LocalAvatarFrame
              role="img"
              aria-label={
                local.name
                  ? intl.formatMessage(msgs.namedLocal, { name: local.name })
                  : intl.formatMessage(msgs.local)
              }
              data-testid="blob-local"
            >
              <LocalAvatar local={local} />
            </S.LocalAvatarFrame>
          )}
          {local.name && <S.Caption aria-hidden>{local.name}</S.Caption>}
        </S.Item>
      )}
      {onReplay && (
        <S.Item>
          <S.RailButton
            type="button"
            onClick={onReplay}
            aria-label={intl.formatMessage(msgs.replay)}
            data-testid="blob-replay"
          >
            <Icon name="replayThin" />
          </S.RailButton>
          <S.Caption aria-hidden>{intl.formatMessage(msgs.replay)}</S.Caption>
        </S.Item>
      )}
      {slow && (
        <S.Item>
          <S.RailButton
            type="button"
            onClick={slow.onReplaySlowly}
            aria-label={intl.formatMessage(msgs.replaySlowly)}
            aria-pressed={slow.isPlayingSlowly}
            data-testid="blob-replay-slowly"
          >
            <Icon name="turtle" />
          </S.RailButton>
          <S.Caption aria-hidden>{intl.formatMessage(msgs.slow)}</S.Caption>
        </S.Item>
      )}
      <S.Item>
        <S.RailButton
          type="button"
          onClick={difficult.onToggle}
          aria-label={intl.formatMessage(msgs.markDifficult)}
          aria-pressed={difficult.isDifficult}
          data-testid="blob-mark-difficult"
        >
          <Icon name={difficult.isDifficult ? 'difficultWordsFilled' : 'difficultWordsOutline'} />
        </S.RailButton>
        <S.Caption aria-hidden>{intl.formatMessage(msgs.difficult)}</S.Caption>
      </S.Item>
      <S.Item>
        <S.RailButton
          type="button"
          onClick={onIKnowThis}
          disabled={isIKnowThisDisabled}
          aria-label={intl.formatMessage(msgs.iKnowThis)}
          data-testid="blob-i-know-this"
        >
          <Icon name="tickOutline" />
        </S.RailButton>
        <S.Caption aria-hidden>{intl.formatMessage(msgs.iKnowThis)}</S.Caption>
      </S.Item>
    </S.Rail>
  );
};
